import { Grid } from "@mui/material";
import { table5Rows } from "features/analytics";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_CONVERSIONS_TREND,
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_EFFECTIVE_RATE_TREND,
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_PRIMARY_METRICS,
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_SESSIONS_CONVERSIONS,
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_SESSIONS_TREND,
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_TIME_SPENT_TREND,
  CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_TOP_INTERACTIONS,
} from "features/analytics/constants/chartKeys";

function AudienceNewVsReturningTab() {
  return (
    <Grid container spacing={AnalyticsSpacing}>
      <Grid item xs={8} sx={{ minHeight: table5Rows }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_PRIMARY_METRICS}
          chart={{
            name: "New & Returning Users - Primary Metrics",
            dimension: [ApiDimension.DIMENSION_VISITOR_TYPE],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
            },
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsCard
          chartKey={
            CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_SESSIONS_CONVERSIONS
          }
          chart={{
            name: "New & Returning Users - Sessions & Conversions",
            dimension: [ApiDimension.DIMENSION_VISITOR_TYPE],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_MACRO_CONVERSION_RATE],
                chart: {
                  [ApiMetric.METRIC_MACRO_CONVERSION_RATE]:
                    ApiYAxisChartType.DOT,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
            },
          }}
        />
      </Grid>

      <Grid item xs={8} container spacing={AnalyticsSpacing}>
        <Grid item xs={6} sx={{ minHeight: 450 }}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_SESSIONS_TREND
            }
            chart={{
              name: "New & Returning Users Sessions Trend",
              dimension: [
                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                ApiDimension.DIMENSION_VISITOR_TYPE,
              ],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              },
            }}
          />
        </Grid>
        <Grid item xs={6} sx={{ minHeight: 450 }}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_EFFECTIVE_RATE_TREND
            }
            chart={{
              name: "New & Returning Users Effective Rate Trend",
              dimension: [
                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                ApiDimension.DIMENSION_VISITOR_TYPE,
              ],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_EFFECTIVE_SESSION_RATE],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              },
            }}
          />
        </Grid>
        <Grid item xs={6} sx={{ minHeight: 450 }}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_TIME_SPENT_TREND
            }
            chart={{
              name: "New & Returning Users Time Spent Trend",
              dimension: [
                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                ApiDimension.DIMENSION_VISITOR_TYPE,
              ],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_SESSION_DURATION],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              },
            }}
          />
        </Grid>
        <Grid item xs={6} sx={{ minHeight: 450 }}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_CONVERSIONS_TREND
            }
            chart={{
              name: "New & Returning Users Conversions Trend",
              dimension: [
                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                ApiDimension.DIMENSION_VISITOR_TYPE,
              ],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              },
            }}
          />
        </Grid>
      </Grid>

      <Grid item xs={4}>
        <StandardAnalyticsCard
          chartKey={
            CHART_KEY_AUDIENCE_COMPARISON_NEW_RETURNING_TOP_INTERACTIONS
          }
          chart={{
            name: "Top Interactions by Visitor Type",
            dimension: [
              ApiDimension.DIMENSION_VISITOR_TYPE,
              ApiDimension.DIMENSION_MICRO_CONVERSION,
            ],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiDimension.DIMENSION_VISITOR_TYPE}`,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              rowsPerPage: 12,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default AudienceNewVsReturningTab;
